// Manage Account adapters (FRONTEND_RULES §2, §7). The UI speaks camelCase only;
// this is the single boundary so components never see a wire key. Two mappers:
//  - `mapAPIToUI`       — deep-camel the mock (already the UI shape) for mock mode.
//  - `mapManageAPIToUI` — the LIVE shape, which genuinely differs (different nesting,
//                         joins fund names onto elections, and synthesizes UI-only
//                         data the API doesn't carry: chart colors, rebalance methods,
//                         per-fund transfer balances, asOf, planMaxPct).

import { formatDate } from '../../../utils/format';
import { FUND_BLUE_RAMP, colorAt } from '../../../utils/chartColors';
// NOTE: the four levers' hand-off destination is NOT part of this view model. It is
// app config, not plan data — the page names a legacy route key per card and
// hooks/useLegacyPortal resolves it (config/legacyPortal.js).

const toCamel = (str) => str.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());

// Deep snake_case → camelCase for object keys. Plain string/number/boolean
// values (tickers, fund colors, dates) pass through untouched.
const deepCamel = (val) => {
  if (Array.isArray(val)) return val.map(deepCamel);
  if (val && typeof val === 'object') {
    return Object.entries(val).reduce((acc, [k, v]) => {
      acc[toCamel(k)] = deepCamel(v);
      return acc;
    }, {});
  }
  return val;
};

/**
 * Map a raw Manage Account API response to the camelCase shape the UI consumes.
 * @param {object|null} api raw snake_case payload
 * @returns {object|null} camelCase UI model (null when there's no payload)
 */
export const mapAPIToUI = (api) => (api ? deepCamel(api) : null);

/**
 * UI → API direction (writes). `org_id` is injected HERE, never in a component
 * (Rule 2).
 */
export const mapUIToAPI = (ui = {}) => ({
  ...ui,
  org_id: localStorage.getItem('pcs_org_id'),
});

// ─── Live backend mapper ────────────────────────────────────────────────────
// Chart colors for allocation/transfer slices — the navy→blue ramp (chart data, NOT
// design tokens), assigned by index. The values now come from `utils/chartColors`,
// which owns every by-position palette; this file used to carry its own copy,
// byte-identical to portfolio.adapters' — one rebrand had to be made twice.

// The three rebalance methods are fixed UI rows; only the recurring row reflects
// live state (auto-rebalance on/off + cadence).
const buildRebalanceMethods = (rb = {}) => [
  {
    key: 'recurring',
    name: 'Recurring rebalance',
    desc: 'Automatically realign on a schedule',
    status: rb.autoOn ? `On · ${(rb.cadence || 'scheduled').toLowerCase()}` : 'Off',
  },
  { key: 'conformBal', name: 'Conform ending balance', desc: 'One-time realignment to match election %', status: 'Available' },
  { key: 'conformTgt', name: 'Conform to target', desc: 'Realign to specified target percentages', status: 'Available' },
];

// Paychecks paid YTD — not in the payload; estimate from the fraction of the year
// elapsed (used only by the take-home/projection math in ContributionsCard).
const estimateChecksPaidYtd = (perYear = 0) => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const end = new Date(now.getFullYear() + 1, 0, 1);
  return Math.max(0, Math.min(perYear, Math.round((perYear * (now - start)) / (end - start))));
};

const round = (n) => Math.round(n);

/**
 * Map the LIVE backend payloads to the camelCase UI model the screen consumes.
 * @param {object} sources
 * @param {object} sources.manage  /manage-account body (the levers)
 * @param {object|null} sources.card  the /overview plan card for this account
 *   (hero balance/vested/status) — the multi-plan PR will own this source
 * @param {object|null} sources.identity  { firstName } from /me (holder name)
 * @param {object|null} [sources.account]  the /me account ref (planName fallback)
 * @returns {object|null} UI model, or null when there's nothing to show.
 */
export const mapManageAPIToUI = ({ manage, card, identity, account } = {}) => {
  if (!manage) return null;

  const cs = manage.contributionSetup || {};
  const irs = manage.irsLimits || {};
  const rb = manage.rebalanceState || {};
  const tr = manage.transferRules || {};
  const perYear = manage.pay?.paychecksPerYear ?? 0;
  // Hero balance comes from the /overview card. When it's unavailable (call
  // failed, or the multi-plan PR hasn't wired its source yet), show "—" rather
  // than a misleading $0 — so `null`, not `0`, for the display fields. A separate
  // numeric (`totalForCalc`) drives the transfer-balance math, which needs a number.
  const hasCard = !!card;
  const totalBalance = hasCard ? card.amount : null;
  const vestedBalance = hasCard ? card.vested ?? card.amount : null;
  const vestedPct = hasCard && card.amount ? round(((card.vested ?? card.amount) / card.amount) * 100) : null;
  const totalForCalc = card?.amount ?? 0;

  const fundsByTicker = (manage.planFunds || []).reduce((acc, f) => {
    acc[f.ticker] = f;
    return acc;
  }, {});

  const allocations = (manage.elections || []).map((e, i) => ({
    ticker: e.ticker,
    name: fundsByTicker[e.ticker]?.name || e.ticker,
    election: e.electionPct,
    actual: e.actualPct,
    electDollar: e.electionDollar,
    color: colorAt(FUND_BLUE_RAMP, i),
  }));

  return {
    account: {
      holderName: identity?.firstName || '',
      planName: card?.name || account?.planName || '',
      totalBalance,
      vestedBalance,
      vestedPct,
      status: card?.status || 'Active',
      // Neither /manage-account nor the overview card carries the balance's as-of date;
      // none is shown rather than a guessed "yesterday".
      asOf: null,
      asOfTime: '4:00 PM ET',
    },
    pay: {
      grossPerCheck: manage.pay?.grossPerCheck ?? 0,
      perYear,
      federalEffective: manage.pay?.federalEffectiveRate ?? 0,
      checksPaidYtd: estimateChecksPaidYtd(perYear),
    },
    contributions: {
      pretaxPct: cs.pretaxPct,
      pretaxDollar: cs.pretaxDollar,
      rothPct: cs.rothPct,
      rothDollar: cs.rothDollar,
      employerMatchPct: cs.employerMatchPct ?? 0,
      employerMatchDollar: cs.employerMatchDollar,
      totalPerCheck: cs.totalPerCheck,
      irsContributedYtd: cs.irsContributedYtd,
      // The dial tracks the employee elective-deferral limit (contributions stop
      // when it's hit), not the higher aggregate limit. Prefer the participant's
      // catch-up-inclusive `applicable` figure (FM-1925) — the flat plan limit
      // understates the cap for anyone 50+. Falls back for payloads without it.
      irsLimit: irs.applicable?.employeeElectiveApplicable ?? irs.employeeElectiveLimit,
      catchUpLimit: irs.catchUpLimit,
      catchUpEligible: cs.catchUpEligible,
      // Plan-level rate cap isn't in the contract — no practical cap until exposed.
      planMaxPct: 100,
      autoIncreaseOn: cs.autoIncreaseOn,
      autoIncreaseAmt: cs.autoIncreaseAmt,
      autoIncreaseNext: cs.autoIncreaseNext ? formatDate(cs.autoIncreaseNext) : null,
      lastChange: cs.lastChange,
      lastChangeDate: cs.lastChangeDate ? formatDate(cs.lastChangeDate) : null,

      // ── Passthrough of the contract that shipped in PR #33 and had no consumer ──
      // Field names deliberately mirror the wire, so `utils/contributions.js` can
      // operate on this object exactly as it would on a raw `contributionSetup`.
      rates: cs.rates ?? null, //                      per-source elected rates (FM-1921)
      projection: cs.projection ?? null, //            year-end vs applicable limit (FM-1926)
      contributionState: cs.contributionState ?? null, // elected vs derived (FM-1922)
      matchAvailability: cs.matchAvailability ?? null, // match confidence (FM-1923)
      employerMatchRate: cs.employerMatchRate ?? null,
      // Formula-funded employer money (cash-balance pay credit + interest credit),
      // which `matchAvailability` cannot express — see `employerCreditNote()`.
      employerCredit: cs.employerCredit ?? null,
      canChangeRate: cs.canChangeRate ?? null, //      self-service gate (FM-1919)
      autoIncreaseAvailable: cs.autoIncreaseAvailable ?? null, // (FM-1920)
      autoIncreaseWebEditable: cs.autoIncreaseWebEditable ?? null,
      // Raw limits incl. the participant-specific `applicable` block, so shared
      // helpers resolve the catch-up-inclusive cap without re-deriving it.
      irsLimits: irs,
    },
    allocations,
    electionRules: manage.electionRules
      ? {
          minPct: manage.electionRules.minPct,
          increment: manage.electionRules.incrementPct,
          processingDays: manage.electionRules.processingDays,
          lastChanged: manage.electionRules.lastChanged ? formatDate(manage.electionRules.lastChanged) : null,
        }
      : {},
    rebalance: {
      netDrift: rb.netDrift ?? 0,
      status: rb.status ?? null,
      pendingTransactions: rb.pendingTransactions ?? 0,
      autoOn: rb.autoOn ?? false,
      cadence: rb.cadence,
      next: rb.nextRebalanceDate ? formatDate(rb.nextRebalanceDate) : null,
      lastRebalance: rb.lastRebalanceDate ? formatDate(rb.lastRebalanceDate) : null,
      lastRebalanceFunds: rb.lastRebalanceFunds,
      methods: buildRebalanceMethods(rb),
    },
    transfer: {
      rules: { types: tr.types, minPct: tr.minPct, minIncrement: tr.minIncrement },
      balances: allocations.map((a) => ({
        ticker: a.ticker,
        name: a.name,
        color: a.color,
        pct: a.actual,
        balance: round((totalForCalc * (a.actual ?? 0)) / 100),
      })),
      cutoffs: tr.cutoffs || [],
      history: (manage.transferHistory || []).map((t) => ({
        date: formatDate(t.date),
        from: t.fromTicker,
        to: t.toTicker,
        amount: t.amount,
      })),
    },
    planFunds: (manage.planFunds || []).map((f) => ({
      ticker: f.ticker,
      name: f.name,
      cls: f.assetClass,
      expense: f.expenseRatio,
      ytd: f.ytd,
      oneYr: f.oneYr,
    })),
    heldTickers: (manage.planFunds || []).filter((f) => f.isHeld).map((f) => f.ticker),
  };
};
